import { useEditor, EditorContent } from "@tiptap/react";
import { Node, Extension, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import { useEffect, useRef } from "react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "@/lib/firebase";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, Undo, Redo, Quote, Image as ImageIcon,
} from "lucide-react";

// TipTap (ProseMirror) rich-text editor, themed for A Homely Abode. Outputs
// HTML. Used for listing/blog descriptions. Mirrors the MeProperty editor:
// bold/italic, H2/H3, lists, quote, link, inline image upload, undo/redo.

// Custom inline image node — no extra package needed.
const InlineImage = Node.create({
  name: "image",
  group: "block",
  atom: true,
  addAttributes() {
    return { src: { default: null }, alt: { default: null } };
  },
  parseHTML() {
    return [{ tag: "img[src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes, {
      style: "width:100%;height:auto;border-radius:2px;margin:16px 0;display:block;",
    })];
  },
  addCommands() {
    return {
      setImage: (attrs) => ({ commands }) => commands.insertContent({ type: this.name, attrs }),
    };
  },
});

// Custom font-size mark (TipTap v2 has no official one) — stores an inline
// font-size on the textStyle mark.
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] };
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize || null,
          renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

// Curated font + size options for the toolbar dropdowns. Fonts are the common
// Microsoft / web-safe families (installed by default on Windows), each with a
// cross-platform fallback stack so they still render on Mac/Linux/mobile.
const FONTS = [
  { label: "Font", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Calibri", value: "Calibri, 'Segoe UI', sans-serif" },
  { label: "Segoe UI", value: "'Segoe UI', Tahoma, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Georgia", value: "Georgia, 'Times New Roman', serif" },
  { label: "Cambria", value: "Cambria, Georgia, serif" },
  { label: "Garamond", value: "Garamond, 'Times New Roman', serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
  { label: "Comic Sans MS", value: "'Comic Sans MS', 'Comic Sans', cursive" },
];
const SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "30px", "36px"];

export default function RichTextEditor({ value, onChange, placeholder = "Write here…" }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
      InlineImage,
      TextStyle,
      FontFamily,
      FontSize,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[200px] px-4 py-3 focus:outline-none text-ink_soft " +
          "[&_h2]:font-serif [&_h2]:text-ink [&_h3]:font-serif [&_h3]:text-ink [&_a]:text-bronze",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next !== current && next !== "") {
      editor.commands.setContent(next, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const uploadAndInsert = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const safe = (file.name || "image").replace(/[^\w.\-]/g, "_");
    const key = `media/content/${Date.now()}-${safe}`;
    const task = uploadBytesResumable(ref(storage, key), file, { contentType: file.type });
    task.on("state_changed", null, (err) => console.error("Upload failed", err), async () => {
      const url = await getDownloadURL(task.snapshot.ref);
      editor.chain().focus().setImage({ src: url, alt: file.name || "image" }).run();
    });
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find((i) => i.type.startsWith("image/"));
    if (imageItem) {
      e.preventDefault();
      uploadAndInsert(imageItem.getAsFile());
    }
  };

  if (!editor) return null;

  const Btn = ({ onClick, active, title, children }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-8 w-8 inline-flex items-center justify-center transition-colors ${
        active ? "bg-bronze text-cream" : "text-ink_soft hover:bg-beige/70"
      }`}
    >
      {children}
    </button>
  );

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    // eslint-disable-next-line no-alert
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-bronze/30 bg-cream focus-within:border-bronze" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-bronze/20 px-2 py-1.5 bg-beige/40">
        <select
          value={editor.getAttributes("textStyle").fontFamily || ""}
          onChange={(e) => {
            const v = e.target.value;
            const chain = editor.chain().focus();
            if (v) chain.setFontFamily(v).run(); else chain.unsetFontFamily().run();
          }}
          title="Font"
          data-testid="rte-font-family"
          className="h-8 text-xs bg-cream border border-bronze/20 text-ink_soft px-1 focus:outline-none focus:border-bronze"
        >
          {FONTS.map((f) => <option key={f.label} value={f.value} style={{ fontFamily: f.value || "inherit" }}>{f.label}</option>)}
        </select>
        <select
          value={editor.getAttributes("textStyle").fontSize || ""}
          onChange={(e) => {
            const v = e.target.value;
            const chain = editor.chain().focus();
            if (v) chain.setFontSize(v).run(); else chain.unsetFontSize().run();
          }}
          title="Font size"
          data-testid="rte-font-size"
          className="h-8 text-xs bg-cream border border-bronze/20 text-ink_soft px-1 focus:outline-none focus:border-bronze"
        >
          <option value="">Size</option>
          {SIZES.map((s) => <option key={s} value={s}>{s.replace("px", "")}</option>)}
        </select>
        <span className="w-px h-5 bg-bronze/20 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><Bold size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><Italic size={15} /></Btn>
        <span className="w-px h-5 bg-bronze/20 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading"><Heading2 size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subheading"><Heading3 size={15} /></Btn>
        <span className="w-px h-5 bg-bronze/20 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><List size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><ListOrdered size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote"><Quote size={15} /></Btn>
        <Btn onClick={setLink} active={editor.isActive("link")} title="Link"><LinkIcon size={15} /></Btn>
        <span className="w-px h-5 bg-bronze/20 mx-1" />
        <Btn onClick={() => fileInputRef.current?.click()} title="Insert image"><ImageIcon size={15} /></Btn>
        <span className="w-px h-5 bg-bronze/20 mx-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo size={15} /></Btn>
      </div>

      <div onPaste={handlePaste}>
        <EditorContent editor={editor} data-placeholder={placeholder} />
      </div>

      <p className="text-[10px] text-olive px-4 py-2 border-t border-bronze/15 bg-beige/40">
        Click the image icon to insert an image, or paste one directly with Ctrl+V.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { uploadAndInsert(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}
